/**
 * Utility functions for handling form and field ID clearing during duplication.
 *
 * When duplicating forms, containers, or entire pages, we must ensure that:
 * 1. All form IDs (Form component's `id` prop) are cleared
 * 2. All form field IDs (field component's `id` prop) are cleared
 *
 * This prevents reusing identifiers which would cause conflicts when
 * the forms are published or submitted to external services.
 *
 * The IDs will be regenerated fresh when the components mount (via their
 * respective useNode hooks that call generateUuidV4 when id is null/empty).
 */

import type { Node, SerializedNode } from '@craftjs/core';
import { isFormFieldNode, isFormNode } from '@builder4/shared/components/Form/formRegistry';

/**
 * Clears the form-related identifiers from a Node's props.
 * - Clears `id` prop for Form nodes
 * - Clears `id` prop for form field nodes
 *
 * @param node - The CraftJS Node to process
 * @returns A new Node with cleared form/field IDs
 */
export const clearFormIdentifiersFromNode = (node: Node): Node => {
  const displayName = node.data.displayName;

  // Check if this is a Form or a form field that needs ID clearing
  if (isFormNode(displayName) || isFormFieldNode(displayName)) {
    return {
      ...node,
      data: {
        ...node.data,
        props: {
          ...node.data.props,
          id: null, // Clear the ID - will be regenerated when the component mounts
        },
      },
    };
  }

  return node;
};

/**
 * Clears the form-related identifiers from a SerializedNode's props.
 * - Clears `id` prop for Form nodes
 * - Clears `id` prop for form field nodes
 *
 * @param node - The serialized node to process
 * @returns A new serialized node with cleared form/field IDs
 */
export const clearFormIdentifiersFromSerializedNode = (node: SerializedNode): SerializedNode => {
  const displayName = node.displayName;

  // Check if this is a Form or a form field that needs ID clearing
  if (isFormNode(displayName) || isFormFieldNode(displayName)) {
    return {
      ...node,
      props: {
        ...node.props,
        id: null, // Clear the ID - will be regenerated when the component mounts
      },
    };
  }

  return node;
};

/**
 * Clears form and field identifiers from all nodes in a SerializedNodes object.
 * Used when duplicating saved sections to ensure forms get fresh identifiers.
 *
 * @param nodes - The object containing all serialized nodes
 * @returns A new object with cleared form/field IDs in all applicable nodes
 */
export const clearFormIdentifiersFromSerializedNodes = (
  nodes: Record<string, SerializedNode>,
): Record<string, SerializedNode> => {
  return Object.fromEntries(
    Object.entries(nodes).map(([id, node]) => [id, clearFormIdentifiersFromSerializedNode(node)]),
  );
};
